import { useState, useEffect, useCallback } from 'react';

/**
 * useFormDraft hook
 * Handles client-side local storage draft saving for master data.
 *
 * @param {string} formKey Unique identifier for the form (e.g. 'customer', 'item')
 * @returns {object} { hasDraft, saveDraft, loadDraft, clearDraft }
 */
export function useFormDraft(formKey) {
  const storageKey = `draft_${formKey}`;
  const [hasDraft, setHasDraft] = useState(false);

  // Check if draft exists on mount/formKey change
  useEffect(() => {
    const draft = localStorage.getItem(storageKey);
    setHasDraft(!!draft);
  }, [storageKey]);

  const saveDraft = useCallback((data) => {
    if (data) {
      localStorage.setItem(storageKey, JSON.stringify(data));
      setHasDraft(true);
    }
  }, [storageKey]);

  const loadDraft = useCallback(() => {
    const draft = localStorage.getItem(storageKey);
    if (draft) {
      try {
        return JSON.parse(draft);
      } catch (e) {
        console.error('Failed to parse form draft:', e);
        return null;
      }
    }
    return null;
  }, [storageKey]);

  const clearDraft = useCallback(() => {
    localStorage.removeItem(storageKey);
    setHasDraft(false);
  }, [storageKey]);

  return {
    hasDraft,
    saveDraft,
    loadDraft,
    getDraft: loadDraft,
    clearDraft
  };
}

export default useFormDraft;
